import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { DashboardSummary, Alert } from "@/lib/types";
import { money, compactMoney } from "@/lib/format";
import { presetRange, PRESET_LABELS, PresetKey, DateRange, formatRange } from "@/lib/dates";
import { downloadCsv } from "@/lib/export";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  TrendingUp,
  ArrowDownToLine,
  Banknote,
  Clock,
  ArrowUpFromLine,
  AlertTriangle,
  Receipt,
  Landmark,
  Wallet,
  ArrowLeftRight,
  Loader2,
  RefreshCw,
  Download,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  PiggyBank,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

const KpiCard: React.FC<{
  label: string;
  value: string;
  icon: React.ElementType;
  accent: string;
  testid: string;
  sub?: string;
  onClick?: () => void;
}> = ({ label, value, icon: Icon, accent, testid, sub, onClick }) => (
  <div
    onClick={onClick}
    className={`border border-slate-200 bg-white p-5 transition-all hover:-translate-y-[1px] hover:shadow-sm ${onClick ? "cursor-pointer" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">{label}</span>
      <Icon className={`h-4 w-4 ${accent}`} strokeWidth={1.5} />
    </div>
    <div className="mt-3 text-2xl font-medium tracking-tight text-slate-900 tabular">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

const severityStyle: Record<string, string> = {
  alta: "border-red-200 bg-red-50 text-red-700",
  media: "border-amber-200 bg-amber-50 text-amber-700",
  baja: "border-slate-200 bg-slate-50 text-slate-600",
};

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState<PresetKey>("todo");
  const [range, setRange] = useState<DateRange>({ date_from: null, date_to: null });
  const [channel, setChannel] = useState<string>("todos");
  const [showAlerts, setShowAlerts] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const params: any = {};
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    if (channel && channel !== "todos") params.channel = channel;
    const [s, a] = await Promise.all([
      client.get("/dashboard/summary", { params }),
      client.get("/dashboard/alerts"),
    ]);
    setData(s.data);
    setAlerts(a.data.alerts);
    setLoading(false);
  }, [range, channel]);

  useEffect(() => {
    load();
  }, [load]);

  const onPreset = (key: PresetKey) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const integ = await client.get("/integrations");
      const connected = integ.data.filter((i: any) => i.status === "connected");
      for (const i of connected) {
        await client.post(`/integrations/${i.provider}/sync`);
      }
      toast.success("Sincronización completada");
      load();
    } catch {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const doExport = async (dataset: string) => {
    try {
      await downloadCsv(dataset, { date_from: range.date_from, date_to: range.date_to });
      toast.success(`Exportado: ${dataset}.csv`);
    } catch {
      toast.error("Error al exportar");
    }
  };

  if (loading && !data)
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
      </div>
    );
  if (!data) return null;
  const k = data.kpis;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle={`Control financiero-operativo · MXN · Periodo: ${formatRange(range)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={preset} onValueChange={(v) => onPreset(v as PresetKey)}>
              <SelectTrigger className="w-40 rounded-none border-slate-300" data-testid="dashboard-date-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_LABELS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={channel} onValueChange={(v) => setChannel(v)}>
              <SelectTrigger className="w-40 rounded-none border-slate-300" data-testid="dashboard-channel-filter">
                <SelectValue placeholder="Canal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los canales</SelectItem>
                {data.canales_disponibles.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <>
                <Input
                  type="date"
                  value={range.date_from || ""}
                  onChange={(e) => setRange({ ...range, date_from: e.target.value })}
                  className="w-40 rounded-none border-slate-300"
                  data-testid="dashboard-date-from"
                />
                <Input
                  type="date"
                  value={range.date_to || ""}
                  onChange={(e) => setRange({ ...range, date_to: e.target.value })}
                  className="w-40 rounded-none border-slate-300"
                  data-testid="dashboard-date-to"
                />
              </>
            )}
            <button
              onClick={syncAll}
              disabled={syncing}
              data-testid="dashboard-sync-button"
              className="flex items-center gap-2 rounded-none border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" strokeWidth={1.5} />}
              Sincronizar
            </button>
            <Select onValueChange={(v) => doExport(v)}>
              <SelectTrigger className="w-36 rounded-none border-slate-300" data-testid="dashboard-export">
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4" strokeWidth={1.5} />
                  <span>Exportar</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ventas">Ventas</SelectItem>
                <SelectItem value="caja">Caja</SelectItem>
                <SelectItem value="egresos">Egresos</SelectItem>
                <SelectItem value="deuda">Deuda</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => setShowAlerts((s) => !s)}
              data-testid="dashboard-alerts-toggle"
              className="relative flex items-center gap-2 rounded-none border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Bell className="h-4 w-4" strokeWidth={1.5} />
              Alertas
              {alerts.length > 0 && (
                <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                  {alerts.length}
                </span>
              )}
            </button>
          </div>
        }
      />

      {/* Alerts */}
      {showAlerts && alerts.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="alerts-block">
          {alerts.map((a) => (
            <button
              key={a.type}
              onClick={() => navigate("/cash")}
              className={`flex items-start gap-3 border px-4 py-3 text-left transition-opacity hover:opacity-90 ${severityStyle[a.severity]}`}
              data-testid={`alert-${a.type}`}
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-sm font-semibold">{a.title}</div>
                <div className="text-xs opacity-80">{a.detail}</div>
                <div className="mt-1 text-xs font-medium tabular">{money(a.amount)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Resultado del periodo (ganancia / pérdida) */}
      <div
        className={`mb-4 flex flex-col gap-1 border p-5 sm:flex-row sm:items-center sm:justify-between ${
          k.resultado_periodo >= 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
        }`}
        data-testid="kpi-resultado-periodo"
      >
        <div>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {k.resultado_periodo >= 0 ? (
              <TrendingUp className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
            ) : (
              <ArrowUpFromLine className="h-4 w-4 text-red-600" strokeWidth={1.5} />
            )}
            Resultado del periodo · {k.resultado_periodo >= 0 ? "Ganancia" : "Pérdida"}
          </div>
          {k.resultado_breakdown && (
            <div className="mt-1.5 text-xs text-slate-600" data-testid="resultado-formula">
              <span className="tabular">
                Ingresos cobrados <strong>{money(k.resultado_breakdown.ingresos_cobrados)}</strong>
                {"  −  "}Comisiones <strong>{money(k.resultado_breakdown.comisiones)}</strong>
                {"  −  "}Gasto operativo <strong>{money(k.resultado_breakdown.gasto_operativo)}</strong>
                {"  −  "}Gasto no operativo <strong>{money(k.resultado_breakdown.gasto_no_operativo)}</strong>
                {"  −  "}Intereses de deuda <strong>{money(k.resultado_breakdown.intereses_deuda)}</strong>
              </span>
              <span className="mt-1 block text-[11px] text-slate-400">
                NO se restan Retiros/utilidades ({money(k.resultado_breakdown.excluye_retiros)}) ni abono a capital de deuda ({money(k.resultado_breakdown.excluye_capital_deuda)}): no son gasto, son reparto/pago de préstamo.
              </span>
            </div>
          )}
        </div>
        <div className={`text-3xl font-semibold tabular ${k.resultado_periodo >= 0 ? "text-emerald-700" : "text-red-600"}`}>
          {money(k.resultado_periodo)}
        </div>
      </div>

      {/* ===== BLOQUE 1 · VENTAS (devengado) ===== */}
      <div className="mb-3 mt-2 flex items-baseline gap-2">
        <h3 className="text-sm font-bold uppercase tracking-[0.13em] text-slate-700">Ventas del periodo</h3>
        <span className="text-xs text-slate-400">base devengado · lo facturado, aún no necesariamente en el banco</span>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label="Vendido (bruto)" value={money(k.total_vendido)} icon={TrendingUp} accent="text-[#DD0000]" testid="kpi-total-vendido" sub="ventas de los marketplaces" />
        <KpiCard label="Comisiones de canal" value={money(k.comisiones_plataforma ?? k.cargos_canal)} icon={Receipt} accent="text-red-600" testid="kpi-comisiones" sub="lo que cobran Shopify/Amazon/ML" />
        <KpiCard label="Venta neta" value={money(k.total_cobrado)} icon={ArrowDownToLine} accent="text-emerald-600" testid="kpi-venta-neta" sub="vendido − comisiones" />
      </div>

      {/* ===== BLOQUE 2 · DINERO REAL / CAJA ===== */}
      <div className="mb-3 mt-6 flex items-baseline gap-2">
        <h3 className="text-sm font-bold uppercase tracking-[0.13em] text-slate-700">Dinero real · Caja</h3>
        <span className="text-xs text-slate-400">base efectivo · dinero que de verdad entró o salió</span>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <KpiCard label="Depositado por canales" value={money(k.depositado_canales ?? k.total_liquidado)} icon={Banknote} accent="text-teal-600" testid="kpi-total-liquidado" sub="de las ventas del periodo, lo ya depositado" />
        <KpiCard label="Otros ingresos" value={money(k.otros_ingresos ?? 0)} icon={PiggyBank} accent="text-emerald-600" testid="kpi-otros-ingresos" sub="ingresos manuales en caja" onClick={() => navigate("/cash")} />
        <KpiCard label="Pendiente por depositar" value={money(k.pendiente_por_liquidar)} icon={Clock} accent="text-amber-600" testid="kpi-pendiente-liquidar" sub="los canales aún te deben" onClick={() => navigate("/receivables")} />
        <KpiCard label="Gastos pagados" value={money(k.total_gastado)} icon={ArrowUpFromLine} accent="text-red-700" testid="kpi-total-gastado" sub="salidas reales (incl. retiros y abono a deuda)" onClick={() => navigate("/cash")} />
        <KpiCard label="Saldo de caja" value={money(k.saldo_caja)} icon={Wallet} accent="text-[#DD0000]" testid="kpi-saldo-caja" sub="entradas − salidas acumuladas" />
      </div>

      {/* ===== DETALLE (colapsable) ===== */}
      <button
        onClick={() => setShowDetail((v) => !v)}
        data-testid="toggle-detalle"
        className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"
      >
        {showDetail ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Detalle avanzado
        <span className="ml-1 text-xs font-normal text-slate-400">(desglose de gastos, deuda, retiros y traspasos)</span>
      </button>
      {showDetail && (
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="detalle-section">
          <KpiCard label="Gasto operativo" value={money(k.gasto_operativo)} icon={Receipt} accent="text-red-600" testid="kpi-gasto-operativo" sub="gastos de operación (parte de Gastos pagados)" />
          <KpiCard label="Gasto no operativo" value={money(k.gasto_no_operativo)} icon={Receipt} accent="text-red-500" testid="kpi-gasto-no-operativo" sub="gastos fuera de la operación (sí restan a la ganancia)" />
          <KpiCard label="Retiros / utilidades" value={money(k.retiros)} icon={ArrowUpFromLine} accent="text-violet-600" testid="kpi-retiros" sub="dinero que sacaste del negocio" />
          <KpiCard label="Pago de deuda" value={money(k.pago_deuda)} icon={Landmark} accent="text-amber-600" testid="kpi-pago-deuda" sub={`Capital ${compactMoney(k.pago_deuda_breakdown.capital)} · Interés ${compactMoney(k.pago_deuda_breakdown.interes)}`} />
          <KpiCard label="Deuda acumulada" value={money(k.deuda_pendiente)} icon={Landmark} accent="text-rose-700" testid="kpi-deuda-acumulada" sub="saldo pendiente total" onClick={() => navigate("/debts")} />
          <KpiCard label="Traspasos internos" value={money(k.traspasos)} icon={ArrowLeftRight} accent="text-sky-600" testid="kpi-traspasos" sub="entre tus cuentas · no es gasto" />
          <KpiCard label="Sin clasificar" value={money(k.sin_clasificar_monto)} icon={AlertTriangle} accent="text-amber-500" testid="kpi-sin-clasificar" sub={`${k.sin_clasificar_count} mov. por revisar`} onClick={() => navigate("/cash")} />
          <KpiCard label="Diferencias" value={money(k.diferencias_monto)} icon={AlertTriangle} accent="text-red-500" testid="kpi-diferencias" sub={`${k.diferencias_count} casos de conciliación`} />
        </div>
      )}

      {/* Nota conceptual */}
      <div className="mt-5 border-l-2 border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="dashboard-nota">
        <strong className="text-slate-700">Cómo leer el dashboard:</strong> las tarjetas de <strong>Ventas</strong> son base
        <em> devengado</em> (lo que facturaste, aunque el dinero llegue después). Las de <strong>Dinero real · Caja</strong> son base
        <em> efectivo</em> (lo que de verdad entró o salió del banco). Las comisiones de canal ya vienen <strong>restadas</strong> de los depósitos,
        por eso "Gastos pagados" NO las incluye (se muestran aparte en "Comisiones de canal").
      </div>

      {/* Charts row 1 */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="flujo-chart">
          <h3 className="mb-4 text-sm font-semibold tracking-tight text-slate-800">Flujo de dinero · últimos 14 días</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.flujo_dinero}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tickFormatter={(v) => compactMoney(v)} tick={{ fontSize: 11, fill: "#94a3b8" }} width={55} />
              <Tooltip formatter={(v: number) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="vendido" stroke="#DD0000" strokeWidth={2} dot={false} name="Vendido" />
              <Line type="monotone" dataKey="cobrado" stroke="#10B981" strokeWidth={2} dot={false} name="Cobrado" />
              <Line type="monotone" dataKey="liquidado" stroke="#14B8A6" strokeWidth={2} dot={false} name="Liquidado" strokeDasharray="4 2" />
              <Line type="monotone" dataKey="egresado" stroke="#EF4444" strokeWidth={2} dot={false} name="Egresado" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-slate-200 bg-white p-5" data-testid="composicion-chart">
          <h3 className="mb-4 text-sm font-semibold tracking-tight text-slate-800">Composición de egresos</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.composicion_egresos} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" tickFormatter={(v) => compactMoney(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} width={110} />
              <Tooltip formatter={(v: number) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 0 }} />
              <Bar dataKey="amount" fill="#B00000" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2: por canal + conciliación */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="canal-chart">
          <h3 className="mb-4 text-sm font-semibold tracking-tight text-slate-800">Comparativo por canal</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.por_canal}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tickFormatter={(v) => compactMoney(v)} tick={{ fontSize: 11, fill: "#94a3b8" }} width={55} />
              <Tooltip formatter={(v: number) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="vendido" fill="#DD0000" name="Vendido" radius={[2, 2, 0, 0]} />
              <Bar dataKey="neto" fill="#10B981" name="Cobrado neto" radius={[2, 2, 0, 0]} />
              <Bar dataKey="fees" fill="#EF4444" name="Comisiones" radius={[2, 2, 0, 0]} />
              <Bar dataKey="pendiente_liquidar" fill="#F59E0B" name="Pend. liquidar" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-slate-200 bg-white p-5" data-testid="conciliacion-block">
          <h3 className="mb-4 text-sm font-semibold tracking-tight text-slate-800">Resumen de conciliación</h3>
          <div className="space-y-3">
            {[
              { k: "conciliado_auto", label: "Conciliado automático", color: "text-emerald-600", icon: CheckCircle2 },
              { k: "conciliado_manual", label: "Conciliado manual", color: "text-sky-600", icon: CheckCircle2 },
              { k: "pendiente", label: "Pendiente", color: "text-amber-600", icon: Clock },
              { k: "con_diferencia", label: "Con diferencia", color: "text-red-600", icon: AlertTriangle },
            ].map((row) => (
              <div key={row.k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <row.icon className={`h-4 w-4 ${row.color}`} strokeWidth={1.5} />
                  {row.label}
                </div>
                <span className="text-lg font-medium tabular text-slate-900">
                  {(data.conciliacion as any)[row.k]}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">
            Resultado operativo: <span className="font-medium text-slate-700">{money(k.resultado_operativo)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
